import type { AgentSchedule, AgentScheduleRun, ScheduleConversation, ScheduleMessage } from '../services/scheduleService';

// UI display types (used by ScheduleHistory, ScheduleItemRow)

// Verdict of a condition-gated scheduled email, parsed from the conversation's
// metadata.conditionResult (written by mcpProxy after the run resolves).
export interface ScheduleConditionVerdict {
  met: boolean | null;   // null = evaluation failed (fail-open sent anyway)
  reason: string | null;
  emailSent: boolean;
}

// A single dated run of a question — the clickable leaf of the tree.
export interface ScheduleOccurrence {
  id: string;          // conversationId — used to load the full conversation
  runId: string;       // AgentScheduleRun id
  title: string;       // "May 19 — 4:11 PM"
  status: string;      // "running", "completed", "failed", "limit_exceeded"
  error?: string | null;
  verdict?: ScheduleConditionVerdict; // only present on condition-gated runs
  content: string;     // AI response preview
}

// A question (runner) grouped across all the dates it has run on.
export interface ScheduleQuestion {
  id: string;          // grouping key — runnerId (or a title-derived fallback)
  question: string;    // runner title (the prompt label)
  runnerId: string;    // link back to AgentScheduleRunner
  occurrences: ScheduleOccurrence[];
}

export interface ScheduleItem {
  id: string;          // AgentSchedule id
  title: string;
  runtime: string;
  active: boolean;
  questions: ScheduleQuestion[];
}

/**
 * Format a date string into "May 19 — 4:11 PM" display format
 */
function formatRunDate(dateStr: string): string {
  const d = new Date(dateStr);
  const month = d.toLocaleDateString('en-US', { month: 'short' });
  const day = d.getDate();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${month} ${day} — ${time}`;
}

/**
 * Parse a conversation's metadata (AWSJSON string) into a condition verdict,
 * or undefined when the run wasn't condition-gated (or metadata is malformed).
 */
function parseConditionVerdict(metadata?: string | null): ScheduleConditionVerdict | undefined {
  if (!metadata) return undefined;
  try {
    const cr = JSON.parse(metadata)?.conditionResult;
    if (!cr || typeof cr !== 'object') return undefined;
    return {
      met: typeof cr.met === 'boolean' ? cr.met : null,
      reason: typeof cr.reason === 'string' ? cr.reason : null,
      emailSent: cr.emailSent === true,
    };
  } catch {
    return undefined;
  }
}

/**
 * Build a runner ID → title lookup from the schedule's runners
 */
function buildRunnerTitleMap(schedule: AgentSchedule): Record<string, string> {
  const map: Record<string, string> = {};
  const runners = schedule.runners?.items || [];
  for (const runner of runners) {
    map[runner.id] = runner.title;
  }
  return map;
}

/**
 * Transform an AgentSchedule + its runs into the UI ScheduleItem shape.
 *
 * The tree is pivoted so that each question (runner) is a parent and the
 * dates it has run on are its children. Runs are expected newest-first, so
 * each question's occurrences come out newest-first as well.
 */
export function transformScheduleToItem(
  schedule: AgentSchedule,
  runs: AgentScheduleRun[]
): ScheduleItem {
  const runnerTitles = buildRunnerTitleMap(schedule);

  // Group conversations by runner, preserving first-seen order of questions.
  const groups = new Map<string, ScheduleQuestion>();

  for (const run of runs) {
    const conversations = run.conversations?.items || [];

    for (const conv of conversations as ScheduleConversation[]) {
      const aiMessage = conv.messages?.items?.find((m: ScheduleMessage) => !m.isUser);
      const runnerTitle = conv.agentScheduleRunnerId
        ? runnerTitles[conv.agentScheduleRunnerId] || conv.title.replace('[Scheduled] ', '')
        : conv.title.replace('[Scheduled] ', '');
      // Scope the grouping key to the schedule so two agents that share a
      // question title (the runnerId-less fallback path) don't collide in the
      // shared expand/selection state keyed on this id.
      const key = `${schedule.id}:${conv.agentScheduleRunnerId || `title:${runnerTitle}`}`;

      let group = groups.get(key);
      if (!group) {
        group = {
          id: key,
          question: runnerTitle,
          runnerId: conv.agentScheduleRunnerId || '',
          occurrences: [],
        };
        groups.set(key, group);
      }

      group.occurrences.push({
        id: conv.id,
        runId: run.id,
        title: formatRunDate(run.createdAt),
        status: run.status,
        error: run.error,
        verdict: parseConditionVerdict(conv.metadata),
        content: aiMessage?.content || 'Waiting for response...',
      });
    }
  }

  return {
    id: schedule.id,
    title: schedule.title,
    runtime: schedule.runtime,
    active: schedule.status === 'ACTIVE',
    questions: Array.from(groups.values()),
  };
}
